import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ROUTES } from '@/lib/constants/routes';
import { ELECTRONIC_SIGNATURE_ENABLED } from '@/lib/constants/featureFlags';
import { useSettingsStore } from '@/store/settingsStore';
import { LoadingSpinner } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import {
  Settings,
  ChevronRight,
  BarChart3,
  Activity,
  BookOpen,
  FileSignature,
} from 'lucide-react';

/**
 * Navigation item interface
 */
interface NavItem {
  label: string;
  path: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  requiresOrganizationPermission?: keyof import('@/features/organizations/api/organization.schemas').OrganizationPermissions;
  requiresProjectPermission?: keyof import('@/features/organizations/api/organization.schemas').ProjectPermissions;
}

/**
 * Sidebar Component
 * 
 * Navigation sidebar with route links and active state highlighting,
 * enhanced with accessibility features.
 * 
 * Responsibilities:
 * - Display navigation menu with proper ARIA labels
 * - Highlight active route
 * - Support nested navigation
 * - Handle mobile collapse/expand with keyboard support
 * - Show/hide items based on permissions
 * - Provide screen reader friendly navigation
 */

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isContextLoading?: boolean;
}

export function Sidebar({ isOpen = true, onClose, isContextLoading = false }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { organizationPermissions, projectPermissions } = useSettingsStore();

  // State for managing section expansion
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Navigation items configuration with permission requirements
  const navItems: NavItem[] = [
    {
      label: t('navigation.analytics'),
      path: '#', // Section header, no direct path
      icon: BarChart3,
      children: [
        {
          label: t('navigation.identityProofingMetrics'),
          path: ROUTES.DASHBOARD, // Keep existing route for Identity Proofing metrics
          requiresProjectPermission: 'canViewTables',
        },
        {
          label: t('navigation.faceVerificationMetrics'),
          path: ROUTES.FACE_RECOGNITION_METRICS,
          requiresProjectPermission: 'canViewTables',
        },
      ],
    },
    {
      label: t('navigation.events'),
      path: '#', // Section header, no direct path
      icon: Activity,
      children: [
        {
          label: t('navigation.identityProofingEvents'),
          path: ROUTES.TABLES.IDENTITY_VALIDATION,
          requiresProjectPermission: 'canViewTables',
        },
        {
          label: t('navigation.faceVerificationEvents'),
          path: ROUTES.TABLES.FACE_RECOGNITION,
          requiresProjectPermission: 'canViewTables',
        },
      ],
    },
    ...(ELECTRONIC_SIGNATURE_ENABLED
      ? [
          {
            label: t('navigation.electronicSignature'),
            path: '#', // Section header, no direct path
            icon: FileSignature,
            children: [
              {
                label: t('navigation.electronicSignatureMetrics'),
                path: ROUTES.ELECTRONIC_SIGNATURE.METRICS,
                requiresProjectPermission: 'canViewMetrics' as const,
              },
              {
                label: t('navigation.electronicSignatureList'),
                path: ROUTES.ELECTRONIC_SIGNATURE.LIST,
                requiresProjectPermission: 'canViewTables' as const,
              },
              {
                label: t('navigation.electronicSignatureSettings'),
                path: ROUTES.ELECTRONIC_SIGNATURE.SETTINGS,
                requiresProjectPermission: 'canManageSettings' as const,
              },
            ],
          },
        ]
      : []),
    {
      label: t('navigation.settings'),
      path: '#', // Section header, no direct path
      icon: Settings,
      children: [
        {
          label: t('navigation.projectSettings'),
          path: ROUTES.PROJECT_SETTINGS.INDEX,
          requiresProjectPermission: 'canManageSettings',
        },
      ],
    },
    {
      label: t('navigation.documentation'),
      path: ROUTES.DOCS.INDEX,
      icon: BookOpen,
    },
  ];

  /**
   * Check if a route is currently active
   */
  const isActive = (path: string): boolean => {
    // Skip section headers (paths with '#')
    if (path === '#') return false;
    // Exact match for dashboard routes to avoid conflicts
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === ROUTES.DASHBOARD;
    }
    if (path === ROUTES.FACE_RECOGNITION_METRICS) {
      return location.pathname === ROUTES.FACE_RECOGNITION_METRICS;
    }
    // For other routes, use exact match or startsWith for sub-routes
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  /**
   * Check if a section should be expanded (has active children or manually expanded)
   */
  const isSectionExpanded = (item: NavItem): boolean => {
    if (!item.children) return false;
    
    // Check if section is manually collapsed
    const isManuallyCollapsed = expandedSections.has(`${item.label}-collapsed`);
    if (isManuallyCollapsed) return false;
    
    // Check if section is manually expanded
    const isManuallyExpanded = expandedSections.has(item.label);
    if (isManuallyExpanded) return true;
    
    // Default: expand if has active child
    const hasActiveChild = item.children.some(child => isActive(child.path));
    return hasActiveChild;
  };

  /**
   * Toggle section expansion
   */
  const toggleSection = (sectionLabel: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      
      // Find the section to check if it has active children
      const section = navItems.find(item => item.label === sectionLabel);
      const hasActiveChild = section?.children?.some(child => isActive(child.path)) || false;
      
      // Clear any existing state for this section
      newSet.delete(sectionLabel);
      newSet.delete(`${sectionLabel}-collapsed`);
      
      // Determine current state
      const isManuallyExpanded = prev.has(sectionLabel);
      const isManuallyCollapsed = prev.has(`${sectionLabel}-collapsed`);
      const isCurrentlyExpanded = isManuallyExpanded || (hasActiveChild && !isManuallyCollapsed);
      
      if (isCurrentlyExpanded) {
        // If currently expanded, mark as manually collapsed
        newSet.add(`${sectionLabel}-collapsed`);
      } else {
        // If currently collapsed, mark as manually expanded
        newSet.add(sectionLabel);
      }
      
      return newSet;
    });
  };

  /**
   * Check if a navigation item should be visible based on permissions.
   * Section headers (path === '#') are visible only if at least one of
   * their children is visible — otherwise the parent renders as an
   * empty/dead label (e.g. "Configuration" still showing for an
   * auditor whose only Settings child is gated to `canManageSettings`).
   */
  const isItemVisible = (item: NavItem): boolean => {
    // Section header: visible iff at least one child is visible.
    if (item.path === '#') {
      if (!item.children || item.children.length === 0) {
        return false;
      }
      return item.children.some(isItemVisible);
    }

    // Leaf item: gate by declared permission requirements.
    if (!item.requiresOrganizationPermission && !item.requiresProjectPermission) {
      return true;
    }

    if (item.requiresOrganizationPermission) {
      if (!organizationPermissions || !organizationPermissions[item.requiresOrganizationPermission]) {
        return false;
      }
    }

    if (item.requiresProjectPermission) {
      if (!projectPermissions || !projectPermissions[item.requiresProjectPermission]) {
        return false;
      }
    }

    return true;
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen w-64 bg-slate-900 border-r border-slate-700/50 transition-transform',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0'
      )}
      aria-label={t('accessibility.mainNavigation', { defaultValue: 'Main navigation' })}
    >
      <div className="flex h-full flex-col">
        {/* Logo/Brand — clicks navigate to the analytics dashboard */}
        <div className="flex h-16 items-center px-6 border-b border-slate-700/50">
          <Link
            to={ROUTES.DASHBOARD}
            className="inline-flex items-center transition-opacity hover:opacity-80"
            aria-label={t('accessibility.goToDashboard', { defaultValue: 'Go to dashboard' })}
          >
            <img
              src="/logo2.png"
              alt={t('accessibility.companyLogo', { defaultValue: 'WYA Company Logo' })}
              className="h-8 w-auto"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav 
          className="flex-1 overflow-y-auto p-4"
          role="navigation"
          aria-label={t('accessibility.primaryNavigation', { defaultValue: 'Primary navigation' })}
        >
          {isContextLoading ? (
            <div 
              role="status" 
              aria-live="polite"
              aria-label={t('accessibility.loadingNavigation', { defaultValue: 'Loading navigation' })}
            >
              <LoadingSpinner size="sm" text={t('common.loading')} className="py-8" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Main Navigation Section */}
              <div>
                <div className="px-3 mb-3">
                  <h3 
                    className="text-xs font-semibold text-slate-400 uppercase tracking-wider"
                    id="main-navigation-heading"
                  >
                    {t('navigation.menu', { defaultValue: 'MENU' })}
                  </h3>
                </div>
                <ul 
                  className="space-y-1"
                  role="list"
                  aria-labelledby="main-navigation-heading"
                >
                  {navItems.filter(isItemVisible).map((item) => {
                    const Icon = item.icon;
                    const itemIsActive = isActive(item.path);
                    const isSection = item.path === '#';
                    const sectionExpanded = isSectionExpanded(item);
                    
                    return (
                      <li key={item.path + item.label} role="none">
                        {isSection ? (
                          // Section header (clickable to expand/collapse)
                          <button
                            onClick={() => toggleSection(item.label)}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 w-full text-left',
                              sectionExpanded
                                ? 'bg-slate-800/50 text-white'
                                : 'text-slate-300 hover:bg-slate-800/30 hover:text-white'
                            )}
                            role="button"
                            aria-expanded={sectionExpanded}
                            aria-controls={`section-${item.label}`}
                          >
                            {Icon && (
                              <Icon 
                                className={cn(
                                  'h-5 w-5 transition-colors',
                                  sectionExpanded 
                                    ? 'text-blue-300' 
                                    : 'text-slate-400'
                                )}
                                aria-hidden="true"
                              />
                            )}
                            <span className="flex-1">{item.label}</span>
                            {item.children && item.children.length > 0 && (
                              <ChevronRight 
                                className={cn(
                                  'h-4 w-4 transition-transform',
                                  sectionExpanded 
                                    ? 'text-blue-300 rotate-90' 
                                    : 'text-slate-400'
                                )}
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        ) : (
                          // Regular navigation link
                          <Link
                            to={item.path}
                            onClick={onClose}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group',
                              itemIsActive
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                                : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                            )}
                            aria-current={itemIsActive ? 'page' : undefined}
                            role="menuitem"
                          >
                            {Icon && (
                              <Icon 
                                className={cn(
                                  'h-5 w-5 transition-colors',
                                  itemIsActive 
                                    ? 'text-white' 
                                    : 'text-slate-400 group-hover:text-blue-300'
                                )}
                                aria-hidden="true"
                              />
                            )}
                            <span className="flex-1">{item.label}</span>
                            {item.children && item.children.length > 0 && (
                              <ChevronRight 
                                className="h-4 w-4 text-slate-400 group-hover:text-blue-300" 
                                aria-hidden="true"
                              />
                            )}
                          </Link>
                        )}

                        {/* Nested navigation items */}
                        {item.children && item.children.length > 0 && sectionExpanded && (
                          <ul 
                            id={`section-${item.label}`}
                            className="ml-8 mt-1 space-y-1"
                            role="list"
                            aria-label={t('accessibility.subNavigation', { 
                              parent: item.label,
                              defaultValue: `${item.label} sub-navigation`
                            })}
                          >
                            {item.children.filter(isItemVisible).map((child) => {
                              const childIsActive = isActive(child.path);
                              
                              return (
                                <li key={child.path} role="none">
                                  <Link
                                    to={child.path}
                                    onClick={onClose}
                                    className={cn(
                                      'flex items-center rounded-lg px-3 py-2 text-sm transition-colors',
                                      childIsActive
                                        ? 'bg-blue-600/20 text-blue-300 font-medium'
                                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-300'
                                    )}
                                    aria-current={childIsActive ? 'page' : undefined}
                                    role="menuitem"
                                  >
                                    {child.label}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
}
